import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const DocumentApiContext = createContext(null);

export function DocumentApiProvider({ children }) {
  const { get, put, del, putUpload } = useApiService(CONFIG.SERVER_DOCUMENT);

  const getDocuments = useCallback(
    async (skip, limit) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        const response = await get(`/v1/documents?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }
    },
    [get],
  );

  const getDocumentById = useCallback(
    async (documentId) => {
      try {
        const response = await get(`/v1/documents/${documentId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getDocumentContent = useCallback(
    async (documentId) => {
      try {
        const response = await get(`/v1/documents/${documentId}/content`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document content:', error);
        throw error;
      }
    },
    [get],
  );

  const getDocumentGraph = useCallback(
    async (documentId) => {
      try {
        const response = await get(`/v1/graph/documents/${documentId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document graph:', error);
        throw error;
      }
    },
    [get],
  );

  const uploadDocument = useCallback(
    async (file) => {
      try {
        const formData = new FormData();
        formData.append('document', file);
        const response = await putUpload('/v1/documents', formData);
        return response.data;
      } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
      }
    },
    [putUpload],
  );

  const uploadDocumentFromLink = useCallback(
    async (link) => {
      try {
        const response = await put('/v1/documents/link', { src: link });
        return response.data;
      } catch (error) {
        console.error('Error uploading document from link:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteDocument = useCallback(
    async (documentId) => {
      try {
        const response = await del(`/v1/documents/${documentId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting document:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <DocumentApiContext.Provider
      value={{
        getDocuments,
        getDocumentById,
        deleteDocument,
        uploadDocument,
        uploadDocumentFromLink,
        getDocumentContent,
        getDocumentGraph,
      }}
    >
      {children}
    </DocumentApiContext.Provider>
  );
}

export function useDocumentApi() {
  const context = useContext(DocumentApiContext);
  if (!context) {
    throw new Error('useDocumentApi must be used within a DocumentApiProvider');
  }
  return context;
}
